import { isArray, isNil, toString } from 'lodash';
// import Component
import CopyToClipboard from 'components/common/CopyToClipboard';
import TittleLink from 'components/common/TittleLink';
// Import UI Components
import { Icon } from '@geneui/components';
// Import Constants
import { RoutesList } from 'routes';
import {
    FilterHolderFields,
    FilterComparisons,
    DynamicInputTypes,
    l,
    CustomerJourneyStatus,
    CustomerJourneyStatusLabels,
    StateModesLabels,
    DataTableColumnsCustomTypes,
    EntityStates,
    ArchiveStates,
    RealtimePromotionTypes,
    PromotionTypesDropdownData,
    PARTNER_SETTINGS,
    BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
} from 'constants/common';
import { Helpers } from 'services';
// Import Enums
import { PermissionEnum } from 'enums';
import { DataTableColumn } from 'types';

const { REALTIME_JOURNEY_VIEW, REALTIME_ACTION_VIEW } = RoutesList;

const { customMomentWithoutTimezoneConversion } = Helpers;
const { In, Equals, Like, Between, IsBlank } = FilterComparisons;
const { textInput, select, rangeDate, multiSelect } = FilterHolderFields;

export interface CustomerJourneyListCustomFieldMetadata {
    CustomFieldId: number;
    Name: string;
    Type: number;
}

interface CustomFieldFilterDropdownOption {
    label: string;
    value: number;
}

interface CustomerJourneyCustomFieldValueModel {
    CustomFieldId: number;
    FieldValue?: Nullable<string>;
    ViewData?: {
        Name?: Nullable<string>;
        Value?: Nullable<string>;
    };
}

type CustomerJourneyCustomFieldsListValueModel = Nullable<
    CustomerJourneyCustomFieldValueModel[] | Record<string, Nullable<string>>
>;

export const getCustomerJourneyCustomFieldColumnDataKey = (customFieldId: number): string => {
    return `CustomField${customFieldId}`;
};

export const getCustomerJourneyCustomFieldFilterValueKey = (customFieldId: number): string => {
    return `CustomField${customFieldId}`;
};

export const getCustomerJourneyCustomFieldFilters = (
    customFields: CustomerJourneyListCustomFieldMetadata[],
    customFieldOptions: Record<number, CustomFieldFilterDropdownOption[]> = {},
) => {
    return customFields.map(({ CustomFieldId, Name, Type }) => {
        if (Type === DynamicInputTypes.DROP_DOWN) {
            return {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: getCustomerJourneyCustomFieldFilterValueKey(CustomFieldId),
                isMultiSelect: true,
                hasSearch: true,
                comparision: In,
                displayName: Name,
                value: multiSelect.defaultValue,
                data: customFieldOptions[CustomFieldId] ?? [],
            };
        }

        return {
            type: textInput.type,
            labelAppearance: 'swap',
            valueFieldKey: getCustomerJourneyCustomFieldFilterValueKey(CustomFieldId),
            comparision: Equals,
            displayName: Name,
            value: textInput.defaultValue,
        };
    });
};

export const getCustomerJourneyCustomFieldColumns = (
    customFields: CustomerJourneyListCustomFieldMetadata[],
    customFieldNameOverrides: Record<number, string> = {},
): DataTableColumn[] => {
    return customFields.map(({ CustomFieldId, Name }) => ({
        text: customFieldNameOverrides[CustomFieldId] ?? Name,
        dataKey: getCustomerJourneyCustomFieldColumnDataKey(CustomFieldId),
        sortable: false,
        isHiddenByDefault: true,
    }));
};

export const mapCustomerJourneyCustomFieldsToRowData = (
    customFields: CustomerJourneyCustomFieldsListValueModel,
): Record<string, string> => {
    if (isNil(customFields)) {
        return {};
    }

    if (isArray(customFields)) {
        return customFields.reduce<Record<string, string>>((acc, field) => {
            const key = getCustomerJourneyCustomFieldColumnDataKey(field.CustomFieldId);
            acc[key] = field?.ViewData?.Value ?? field?.FieldValue ?? '';
            return acc;
        }, {});
    }

    return Object.entries(customFields).reduce<Record<string, string>>((acc, [key, value]) => {
        acc[key] = value ?? '';
        return acc;
    }, {});
};

export const getCustomerJourneyCustomFieldNamesFromRowData = (
    customFields: CustomerJourneyCustomFieldsListValueModel,
): Record<number, string> => {
    if (!isArray(customFields)) {
        return {};
    }

    return customFields.reduce<Record<number, string>>((acc, field) => {
        if (field?.ViewData?.Name) {
            acc[field.CustomFieldId] = field.ViewData.Name;
        }
        return acc;
    }, {});
};

export const getCustomerJourneyStaticFilterInputsDataList = () => {
    return FilterHolderFieldsConfigs.defaultInputsData.defaultInputsDataList.map((item) => ({
        ...item,
        data: isArray(item.data) ? [...item.data] : item.data,
    }));
};

export const getTitle = (row) => {
    return row.data.Name.Name;
};

export const FilterHolderFieldsConfigs = {
    idFieldName: 'WorkflowId',
    storeKey: 'customerJourneyList',
    defaultInputsData: {
        defaultInputsDataIds: {
            CategoryId: 4,
        },
        defaultInputsDataList: [
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'WorkflowId',
                comparision: Equals,
                displayName: l.WorkflowId,
                value: textInput.defaultValue,
            },
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'Name',
                comparision: Like,
                displayName: l.Title,
                value: textInput.defaultValue,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'Status',
                comparision: Equals,
                displayName: l.Status,
                value: select.defaultValue,
                data: CustomerJourneyStatus,
            },
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CreatorName',
                comparision: Like,
                displayName: l.User,
                value: textInput.defaultValue,
            },
            {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CategoryId',
                isMultiSelect: true,
                hasSearch: true,
                comparision: In,
                displayName: l.Label,
                value: multiSelect.defaultValue,
                data: [],
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'IsNonMarketing',
                comparision: Equals,
                displayName: l.NonMarketing,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
                permissions: PermissionEnum.ManageNonMarketingCampaign,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'IsPublic',
                comparision: Equals,
                displayName: l.Public,
                value: select.defaultValue,
                data: BOOLEAN_DROPDOWN_OPTIONS_FOR_FILTER,
                partnerSettings: PARTNER_SETTINGS.IsAllowABTest,
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CreatedDate',
                comparision: Between,
                popoverAlign: 'start',
                displayName: l.CreatedDate,
                value: rangeDate.defaultValue,
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'StartDate',
                comparision: Between,
                popoverAlign: 'start',
                displayName: l.AutoStart,
                value: rangeDate.defaultValue,
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'EndDate',
                comparision: Between,
                popoverAlign: 'end',
                displayName: l.AutoEnd,
                value: rangeDate.defaultValue,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'IsUsed',
                comparision: Equals,
                displayName: l.State,
                value: select.defaultValue,
                data: EntityStates,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'ArchivedDate',
                comparision: IsBlank,
                displayName: l.Archive,
                value: select.defaultValue,
                data: ArchiveStates,
            },
            {
                type: select.type,
                labelAppearance: 'swap',
                valueFieldKey: 'PromotionType',
                comparision: Equals,
                displayName: l.PromotionType,
                value: select.defaultValue,
                data: PromotionTypesDropdownData,
            },
        ],
    },
};

export const dataTableColumnsConfigs = (t, dateFormats, customFieldColumns: DataTableColumn[] = []) => {
    const { partnerDateTimeFormat } = dateFormats;
    return {
        columns: [
            {
                text: t(l.WorkflowId),
                dataKey: 'WorkflowId',
                sortable: true,
                colRenderer: (id) => <CopyToClipboard>{id}</CopyToClipboard>,
            },
            {
                text: t(l.Title),
                dataKey: 'Name',
                sortable: true,
                isPermanent: true,
                // eslint-disable-next-line react/prop-types
                colRenderer: ({ Name, WorkflowId, Status, HasVisualization, PromotionType }) => {
                    const path =
                        PromotionType === +RealtimePromotionTypes.ActionCampaign
                            ? REALTIME_ACTION_VIEW
                            : REALTIME_JOURNEY_VIEW;
                    const isStarting = +Status === +CustomerJourneyStatusLabels.Starting;
                    const isUpdating = +Status === +CustomerJourneyStatusLabels.Updating;
                    const isResuming = +Status === +CustomerJourneyStatusLabels.Resuming;
                    const isSuspending = +Status === +CustomerJourneyStatusLabels.Suspending;
                    const isMiddle = () => isUpdating || isStarting || isResuming || isSuspending;
                    const isClickable = Status !== +CustomerJourneyStatusLabels.Invalid && !isMiddle();
                    return (
                        <div className="crm-list-column-title-icon-components">
                            <TittleLink
                                isClickable={isClickable}
                                path={path}
                                name={Name}
                                queryParams={{ workflowId: WorkflowId }}
                            />
                            {HasVisualization && <Icon type="bc-icon-visualization" />}
                        </div>
                    );
                },
            },
            {
                text: t(l.User),
                dataKey: 'CreatorName',
                sortable: true,
            },
            {
                text: t(l.Status),
                dataKey: 'Status',
                sortable: true,
                colRenderer: (status) => (
                    <div className={`crm-status-badge-cnt crm-cj-status-label-${status}`}>
                        {t(CustomerJourneyStatusLabels[status])}
                    </div>
                ),
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.CreatedDate),
                dataKey: 'CreatedDate',
                sortable: true,
                formatter: (CreatedDate) => {
                    return (
                        CreatedDate && customMomentWithoutTimezoneConversion(CreatedDate).format(partnerDateTimeFormat)
                    );
                },
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.AutoStart),
                dataKey: 'StartDate',
                sortable: true,
                formatter: (StartDate) => {
                    return StartDate && customMomentWithoutTimezoneConversion(StartDate).format(partnerDateTimeFormat);
                },
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.AutoEnd),
                dataKey: 'EndDate',
                sortable: true,
                formatter: (EndDate) => {
                    return EndDate && customMomentWithoutTimezoneConversion(EndDate).format(partnerDateTimeFormat);
                },
            },
            {
                text: t(l.Mode),
                dataKey: 'State',
                sortable: true,
                colRenderer: (state) => (
                    <div className={`crm-mode-badge-cnt crm-cj-mode-label-${state}`}>{t(StateModesLabels[state])}</div>
                ),
            },
            {
                text: t(l.State),
                dataKey: 'IsUsed',
                sortable: true,
                colRenderer: (isUsed) => (
                    <div className={`crm-used-or-not-used-badge-cnt crm-used-or-not-used-label-${isUsed}`}>
                        {isUsed ? t(l.Used) : t(l.NotUsed)}
                    </div>
                ),
            },
            {
                text: t(l.NonMarketing),
                dataKey: 'IsNonMarketing',
                sortable: true,
                colRenderer: (IsNonMarketing) => (IsNonMarketing ? t(l.Yes) : t(l.No)),
                permissions: [PermissionEnum.ManageNonMarketingCampaign],
            },
            {
                text: t(l.Public),
                dataKey: 'IsPublic',
                sortable: true,
                colRenderer: (IsPublic) => (IsPublic ? t(l.Yes) : t(l.No)),
                partnerSettings: [PARTNER_SETTINGS.IsAllowABTest],
            },
            {
                text: t(l.PromotionType),
                dataKey: 'PromotionType',
                sortable: true,
                colRenderer: (promotionType) => <div>{t(RealtimePromotionTypes[promotionType])} </div>,
            },
            ...customFieldColumns,
        ],
    };
};

const isAllow = (workflow) => {
    const { Status, IsUsed, ArchivedDate, IsPublic, IsNonMarketing } = workflow;
    const status = toString(Status);

    const isNonStarted = status === CustomerJourneyStatusLabels.NonStarted;
    const isInvalid = status === CustomerJourneyStatusLabels.Invalid;
    const isInProgress = status === CustomerJourneyStatusLabels.InProgress;
    const isArchived = !isNil(ArchivedDate);

    const isDeletable = (isNonStarted || isInvalid) && !IsUsed;

    return {
        delete: isDeletable,
        archive: !isDeletable && !isInProgress && !isArchived,
        unarchive: isArchived,
        makePublic: !IsPublic && !IsNonMarketing,
        makePrivate: IsPublic && !IsNonMarketing,
    };
};

export const getCustomerJourneysNamesIds = (selectedRows) => {
    if (!isArray(selectedRows)) return;

    type CustomerJourney = {
        workflowId: string;
        name: string;
    };

    const allowDeleteCustomerJourneys: CustomerJourney[] = [];
    const notAllowDeleteCustomerJourneys: CustomerJourney[] = [];
    const allowArchiveCustomerJourneys: CustomerJourney[] = [];
    const notAllowArchiveCustomerJourneys: CustomerJourney[] = [];
    const allowUnarchiveCustomerJourneys: CustomerJourney[] = [];
    const notAllowUnarchiveCustomerJourneys: CustomerJourney[] = [];
    const allowMakePublicCustomerJourneys: CustomerJourney[] = [];
    const notAllowMakePublicCustomerJourneys: CustomerJourney[] = [];
    const allowMakePrivateCustomerJourneys: CustomerJourney[] = [];
    const notAllowMakePrivateCustomerJourneys: CustomerJourney[] = [];

    selectedRows.forEach(({ data }) => {
        const customerJourneys: CustomerJourney = { workflowId: data.WorkflowId, name: data.Name.Name };
        const isAllowData = isAllow(data);

        if (isAllowData.delete) {
            allowDeleteCustomerJourneys.push(customerJourneys);
        } else {
            notAllowDeleteCustomerJourneys.push(customerJourneys);
        }

        if (isAllowData.archive) {
            allowArchiveCustomerJourneys.push(customerJourneys);
        } else {
            notAllowArchiveCustomerJourneys.push(customerJourneys);
        }

        if (isAllowData.unarchive) {
            allowUnarchiveCustomerJourneys.push(customerJourneys);
        } else {
            notAllowUnarchiveCustomerJourneys.push(customerJourneys);
        }

        if (isAllowData.makePublic) {
            allowMakePublicCustomerJourneys.push(customerJourneys);
        } else {
            notAllowMakePublicCustomerJourneys.push(customerJourneys);
        }

        if (isAllowData.makePrivate) {
            allowMakePrivateCustomerJourneys.push(customerJourneys);
        } else {
            notAllowMakePrivateCustomerJourneys.push(customerJourneys);
        }
    });

    return {
        allowDeleteCustomerJourneys,
        notAllowDeleteCustomerJourneys,
        allowArchiveCustomerJourneys,
        notAllowArchiveCustomerJourneys,
        allowUnarchiveCustomerJourneys,
        notAllowUnarchiveCustomerJourneys,
        allowMakePublicCustomerJourneys,
        notAllowMakePublicCustomerJourneys,
        allowMakePrivateCustomerJourneys,
        notAllowMakePrivateCustomerJourneys,
    };
};
